'use client';

import { useRouter } from 'next/navigation';
import type { FC } from 'react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { MediaType } from '@/helpers/enums';
import { ReceiveBonusesNotice, useCMSConfig } from '@/modules/common';
import { useGetCommonStore } from '@/store';
import { ScContainer } from '@/styles/common';
import { type ICardHudConfig, pickResponsiveSrc } from '../../helpers';
import { MissionLevels } from '../MissionLevels';
import type { ILevelConfig } from '../MissionLevels/helpers';
import { useMissionLevels } from '../MissionLevels/hooks/useMissionLevels';
import { BackgroundImage } from './components/BackgroundImage';
import { CharacterImage } from './components/CharacterImage';
import { CtaButton } from './components/CtaButton';
import { formatPromoDate, getLocale } from './helpers';
import { ScHeadlineHero } from './styled';

interface IHeroProps {
  disableAnimations?: boolean;
  levelsConfig: ILevelConfig[];
  levelsMobileBreakpoint?: MediaType;
  groupPrefix?: string;
  useFullNameAsGroupId?: boolean;
  namespace: string;
  promoKey: string;
  cardHud: ICardHudConfig;
  dateFormat?: string;
  progressBarVariant?: 'pill';
  ctaAlwaysDeposit?: boolean;
}

export const Hero: FC<IHeroProps> = ({
  disableAnimations,
  groupPrefix,
  useFullNameAsGroupId,
  levelsConfig,
  levelsMobileBreakpoint,
  namespace,
  promoKey,
  cardHud,
  dateFormat,
  progressBarVariant,
  ctaAlwaysDeposit,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(namespace);
  const { push } = useRouter();

  const useCommonStore = useGetCommonStore();
  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const { cmsConfig } = useCMSConfig();
  const locale = getLocale(language);
  const dateRange = cmsConfig?.headlinePromo(promoKey).dateRange;
  const startDate =
    dateRange?.start && formatPromoDate(dateRange.start, locale, dateFormat);
  const endDate =
    dateRange?.end && formatPromoDate(dateRange.end, locale, dateFormat);

  const { levels, currentLevelIndex } = useMissionLevels(
    levelsConfig,
    groupPrefix,
    useFullNameAsGroupId
  );
  const currentLevel = levels[currentLevelIndex];
  const isNotStarted = currentLevelIndex === 0 && currentLevel?.percent === 0;

  const [viewedLevelIndex, setViewedLevelIndex] = useState(currentLevelIndex);

  if (!levelsConfig.length) return null;

  const levelIndex = isAuthenticated ? viewedLevelIndex : 0;
  const currentLevelConfig = levelsConfig[levelIndex] ?? levelsConfig[0];
  const background = pickResponsiveSrc(currentLevelConfig.bg, isMobileDevice);

  const handleClaim = () => {
    push('/promotions?tab=active-bonuses');
  };

  return (
    <ScHeadlineHero>
      <BackgroundImage disableAnimation={disableAnimations} src={background} />
      <div className="bonuses-notice-box">
        <ReceiveBonusesNotice
          successMessage="canIssueBonuses.success"
          translation="promotions"
          warningMessage="canIssueBonuses.warning"
        />
      </div>
      <ScContainer>
        <div className="content">
          <h1 className="title">{t('pageTitle')}</h1>
          <p className="description">
            <Trans
              components={{
                span: <span />,
              }}
              i18nKey="description"
              ns={namespace}
              values={{
                startDate,
                endDate,
              }}
            />
          </p>
        </div>

        <CharacterImage
          disableAnimation={disableAnimations}
          src={currentLevelConfig.characterImage}
        />

        <MissionLevels
          cardHud={cardHud}
          defaultLevelIndex={currentLevelIndex}
          levels={levels}
          mobileBreakpoint={levelsMobileBreakpoint}
          namespace={namespace}
          onClaim={handleClaim}
          onLevelChange={setViewedLevelIndex}
          progressBarVariant={progressBarVariant}
        />

        <CtaButton
          alwaysDeposit={ctaAlwaysDeposit}
          isNotStarted={isNotStarted}
          namespace={namespace}
        />
      </ScContainer>
    </ScHeadlineHero>
  );
};
